import { BookingChain } from '@services/api/booking/booking-chain.model';
import { BookingChainService } from '@services/api/booking/booking-chain.service';
import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import { map, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';

export interface ChainCandidate {
  readonly booking: IBookingModel;
  readonly id: number;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly nights: number;
  readonly guestName: string;
  readonly flatLabel: string | null;
  readonly statusName: string;
  readonly sameFlat: boolean;
  readonly alreadyChained: boolean;
}

/**
 * A lánc végére fűzhető jelölteket kéri le és rendezi.
 * Adjacencia-szabály: a jelölt start_date-je pontosan a lánc végének end_date-je.
 * A backend ezt NEM validálja, ezért itt kötelező őr.
 */
@Injectable({
  providedIn: 'root'
})
export class BookingChainLinkService {
  private readonly bookingClient = inject(BookingClientService);
  private readonly chainService = inject(BookingChainService);

  loadCandidates(chain: BookingChain): Observable<ChainCandidate[]> {
    const tail = this.chainService.tailOf(chain);
    const anchorDate = format(tail.endDate, 'yyyy-MM-dd');
    const chainIds = new Set(chain.links.map((link) => link.id));
    return this.bookingClient
      .getAllItems('', { filters: { start_date: anchorDate }, order: { id: 'asc' } })
      .pipe(map((bookings) => this.toCandidates(bookings, anchorDate, tail.flatLabel, chainIds)));
  }

  link(candidateId: number, previousIri: string): Observable<IBookingModel> {
    return this.bookingClient.setPreviousBooking(candidateId, previousIri);
  }

  detach(linkId: number): Observable<IBookingModel> {
    return this.bookingClient.setPreviousBooking(linkId, null);
  }

  private toCandidates(bookings: IBookingModel[], anchorDate: string, tailFlat: string | null, chainIds: Set<number>): ChainCandidate[] {
    return bookings
      .filter((booking) => !chainIds.has(booking.id) && this.startsOn(booking, anchorDate))
      .map((booking) => this.toCandidate(booking, tailFlat))
      .sort((left, right) => Number(right.sameFlat) - Number(left.sameFlat) || left.id - right.id);
  }

  private startsOn(booking: IBookingModel, anchorDate: string): boolean {
    const startDate = parseISO(booking.start_date ?? '');
    return isValid(startDate) && format(startDate, 'yyyy-MM-dd') === anchorDate;
  }

  private toCandidate(booking: IBookingModel, tailFlat: string | null): ChainCandidate {
    const startDate = parseISO(booking.start_date ?? '');
    const endDate = parseISO(booking.end_date ?? '');
    const flatLabel = this.flatLabelOf(booking);
    return {
      booking,
      id: booking.id,
      startDate,
      endDate,
      nights: isValid(startDate) && isValid(endDate) ? Math.max(0, differenceInCalendarDays(endDate, startDate)) : 0,
      guestName: booking.name ?? '',
      flatLabel,
      statusName: booking.bookingStatus?.name ?? '',
      sameFlat: flatLabel !== null && flatLabel === tailFlat,
      alreadyChained: !!booking.previousBooking
    };
  }

  private flatLabelOf(booking: IBookingModel): string | null {
    const flat = booking.flats?.[0];
    if (!flat) {
      return null;
    }
    return `${flat.name ?? ''} ${flat.flat_number ?? ''}`.trim() || null;
  }
}
